/**
 * tierModelResolver — pure helpers for mapping a user's quality-tier choice
 * (Economy / Standard / Premium) onto a concrete `selectedModels` map that
 * downstream code paths (aiService, videoPayloadBuilder, Storyboard shot
 * stamping, …) consume via `localStorage.selectedAIModels`.
 *
 * Selection contract (per capability, in order):
 *   1. If the capability exposes `tiers[<tierId>]` with ≥ 1 entry, use
 *      `tiers[<tierId>][0]`.
 *   2. Else fall back to the highest-priority entry in `capability.models`.
 *      Priority is read from `model.priority` / `rank` / `order` /
 *      `sort_order` (ascending — 1 is top). If none is present, catalog
 *      order is used (models[0]).
 *
 * Why not cost-based for the fallback? The user explicitly requested
 * "If the capability has no tiers, then use the models based on the
 * highest priority ranking. Fallback models should be based on the next
 * priority ranking." — i.e. priority wins over cost here. This mirrors
 * the behaviour already captured by `pickTierModelByPriority` inside
 * TierSelection.jsx (Feb 13, 2026 change).
 */

export const TIER_IDS = ['economy', 'standard', 'premium'];

/** Return `tiers[<tierId>][0]` or null when no entry exists. */
export const pickTierStrict = (capability, tierId) => {
    if (!capability) return null;
    const list = capability.tiers?.[tierId];
    if (Array.isArray(list) && list.length > 0) return list[0];
    return null;
};

/**
 * Return the highest-priority model in `capability.models`.
 * Models are sorted ascending by any of `priority`/`rank`/`order`/
 * `sort_order`; ties preserve catalog order. Models with no priority
 * field sink to the end.
 */
export const pickByPriority = (capability) => {
    if (!capability) return null;
    const models = Array.isArray(capability.models) ? capability.models.slice() : [];
    if (models.length === 0) return null;
    const priorityOf = (m) => {
        for (const f of ['priority', 'rank', 'order', 'sort_order']) {
            const v = m?.[f];
            if (v === null || v === undefined) continue;
            const n = Number(v);
            if (Number.isFinite(n)) return n;
        }
        return Number.POSITIVE_INFINITY;
    };
    models.sort((a, b) => priorityOf(a) - priorityOf(b));
    return models[0] || null;
};

/**
 * Choose the model for `(capability, tierId)` following the two-step
 * contract above. Returns a `{ model, source }` object where `source`
 * is `'tier'` or `'priority'`, or `null` when the capability has no
 * usable entry.
 */
export const resolveCapabilityModel = (capability, tierId) => {
    const strict = pickTierStrict(capability, tierId);
    if (strict) return { model: strict, source: 'tier' };
    const fallback = pickByPriority(capability);
    if (fallback) return { model: fallback, source: 'priority' };
    return null;
};

/**
 * Build a `{ capKey: modelId }` map for every capability given the user's
 * tier choice, write it to localStorage along with helper keys the
 * downstream resolvers rely on, and dispatch the same-tab
 * `ai-models-changed` event so Storyboard re-stamps its shots.
 *
 * Returns `{ selectedModels, tiered, priority, missing }` for callers
 * (and tests) that need to inspect the picker's reasoning.
 */
export const buildSelectedModelsForTier = (capabilities, tierId) => {
    // Seed from any prior storage so capabilities the admin hasn't returned
    // on this fetch keep whatever value they had (defensive — in practice
    // the fetch always returns the full catalog).
    let out = {};
    try {
        const prior = JSON.parse(localStorage.getItem('selectedAIModels') || '{}');
        if (prior && typeof prior === 'object') out = { ...prior };
    } catch { /* noop */ }

    const tiered = [];
    const priority = [];
    const missing = [];

    const caps = Array.isArray(capabilities) ? capabilities : [];
    for (const cap of caps) {
        if (!cap?.key) continue;
        const picked = resolveCapabilityModel(cap, tierId);
        if (!picked) { missing.push(cap.key); continue; }
        out[cap.key] = picked.model.key || picked.model.id;
        (picked.source === 'tier' ? tiered : priority).push(cap.key);
    }

    // Also persist the full tier catalog so modelHelper / aiService can walk
    // the next-priority models if the chosen one fails at runtime.
    const tierData = {};
    for (const cap of caps) {
        if (cap?.tiers && typeof cap.tiers === 'object') tierData[cap.key] = cap.tiers;
    }

    try {
        localStorage.setItem('selectedAIModels', JSON.stringify(out));
        localStorage.setItem('selectedAITier', tierId);
        localStorage.setItem('aiCapabilityTiers', JSON.stringify(tierData));
        window.dispatchEvent(new CustomEvent('ai-models-changed', { detail: out }));
    } catch { /* noop */ }

    if (missing.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
            `[TierPicker] Tier "${tierId}" has no model for capabilities:`,
            missing,
            '— neither tiers[] nor models[] populated. Admin catalog gap.'
        );
    }
    if (priority.length > 0) {
        // eslint-disable-next-line no-console
        console.info(
            `[TierPicker] Tier "${tierId}" used highest-priority fallback for:`,
            priority,
            '— these capabilities have no models mapped at this tier.'
        );
    }

    return { selectedModels: out, tiered, priority, missing };
};

/**
 * Non-destructive preview — same resolution logic as
 * `buildSelectedModelsForTier` but returns a UI-ready list without
 * touching localStorage.
 */
export const previewTierSelection = (capabilities, tierId) => {
    const caps = Array.isArray(capabilities) ? capabilities : [];
    const out = [];
    for (const cap of caps) {
        if (!cap?.key) continue;
        const picked = resolveCapabilityModel(cap, tierId);
        if (!picked) continue;
        out.push({
            capabilityKey: cap.key,
            capability: cap.name || cap.key,
            model: picked.model.name || picked.model.key || picked.model.id || '(unnamed model)',
            tiered: picked.source === 'tier',
        });
    }
    return out;
};
